import React, { useState } from 'react'
import { Button } from 'antd'

/**
 * Renders an antd Button that executes a Promise when clicked. Functions attached to then and catch
 * properties on the JSX element are forwarded to the promise.
 *
 * @param {Function} onClick Function to execute; must return a Promise
 * @param {Function} then Function to execute on Promise fulfillment; result is passed as argument
 * @param {Function} catch Function to execute on Promise rejection; error is passed as argument
 * @param {boolean} loading Whether or not the button should be disabled in a loading state (default false)
 * @returns Button JSX
 */
const AsyncButton = ({ onClick, loading, then, catch: onError, ...rest }) => {
  const [isWorking, setWorking] = useState(false)
  return (
    <Button {...rest}
      loading={loading === undefined ? isWorking : loading}
      onClick={async (...args) => {
        if (typeof onClick === 'function' && !isWorking) {
          let promise = onClick(...args)
          setWorking(true)
          // Wrap the promise so we can intercept fulfillment and set working state to false when
          // the promise is fulfilled
          promise = promise.then(value => {
            setWorking(false)
            if (typeof then === 'function') then(value)
          })
          if (typeof onError === 'function') promise.catch(err => {
            setWorking(false)
            onError(err)
          })
        }
      }}
    />
  )
}

export default AsyncButton
